import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardLayout from "../../components/DashboardLayout";
import DiploksContextHint from "../../components/DiploksContextHint";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Users, CalendarRange, ScrollText, Award,
  GraduationCap, ArrowUpRight, ClipboardList,
} from "lucide-react";

export default function FacultyAdvisorDashboard() {
  const { user } = useAuth();
  const [delegates, setDelegates] = useState([]);
  const [papers, setPapers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [certs, setCerts] = useState([]);

  useEffect(() => {
    const q = user?.school_id ? `school_id=${user.school_id}&` : "";
    api.get(`/v2/delegates?${q}limit=50`).then((r) => setDelegates(r.data.items || [])).catch(() => setDelegates([]));
    api.get("/v2/position-papers?limit=20").then((r) => setPapers(r.data.items || [])).catch(() => setPapers([]));
    api.get("/v2/schedules?limit=10").then((r) => setSchedules(r.data.items || [])).catch(() => setSchedules([]));
    api.get("/v2/certificates?limit=20").then((r) => setCerts(r.data.items || [])).catch(() => setCerts([]));
  }, [user]);

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Faculty Advisor
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            Welcome, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="mt-2 text-slate-500 text-sm">
            Track your delegation's progress across events, papers, and awards.
          </p>
        </motion.div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Tile icon={Users} label="Students" value={delegates.length} />
          <Tile icon={ScrollText} label="Papers" value={papers.length} />
          <Tile icon={Award} label="Awards" value={certs.length} />
          <Tile icon={CalendarRange} label="Schedules" value={schedules.length} />
        </div>

        {/* Faculty-side Diploks context — read-only, no top-up access. */}
        <div className="mt-6">
          <DiploksContextHint
            context="premium delegation reports and bulk certificate exports"
            testId="faculty-diploks-context-hint"
          />
        </div>

        <div className="mt-10 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-6">
            <div className="rounded-xl bg-white border border-slate-200 p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold tracking-tight text-[#0A192F]">
                  Recent position papers
                </h2>
                <Link to="/dashboard/faculty/schedule">
                  <Button variant="outline" className="border-[#0F4C81] text-[#0F4C81]" data-testid="view-schedule-button">
                    Schedule <ArrowUpRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
              <div className="mt-5 divide-y divide-slate-100">
                {papers.slice(0, 6).map((p) => (
                  <div key={p.id} className="py-3 flex items-center justify-between" data-testid={`paper-row-${p.id}`}>
                    <div>
                      <p className="text-sm font-semibold text-[#0A192F]">{p.country_name || "—"} · {p.topic}</p>
                      <p className="text-xs text-slate-500">{p.delegate_name} · {p.status}</p>
                    </div>
                    {p.ai_evaluation?.overall_score != null && (
                      <span className="font-mono text-xs text-[#D4AF37] font-bold">
                        {p.ai_evaluation.overall_score}/100
                      </span>
                    )}
                  </div>
                ))}
                {papers.length === 0 && (
                  <p className="py-8 text-center text-slate-500 text-sm">No papers yet.</p>
                )}
              </div>
            </div>

            <div className="rounded-xl bg-white border border-slate-200 p-6">
              <h2 className="text-lg font-semibold tracking-tight text-[#0A192F]">
                Awards & certificates
              </h2>
              <div className="mt-5 grid md:grid-cols-2 gap-3">
                {certs.slice(0, 6).map((c) => (
                  <div key={c.id} className="p-4 rounded-md bg-slate-50 border border-slate-200" data-testid={`cert-row-${c.code}`}>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">{c.award_type}</p>
                    <p className="mt-1 text-sm font-semibold text-[#0A192F]">{c.delegate_name}</p>
                    <p className="text-xs text-slate-500 font-mono mt-1">{c.code}</p>
                  </div>
                ))}
                {certs.length === 0 && (
                  <p className="text-sm text-slate-500 col-span-2">No awards recorded yet.</p>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 space-y-6">
            <Card className="bg-[#0A192F] text-white border-[#D4AF37]/30">
              <CardContent className="p-6">
                <ClipboardList className="h-5 w-5 text-[#D4AF37]" />
                <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                  Your delegation
                </p>
                <h3 className="mt-2 text-2xl font-semibold tracking-tight">{delegates.length} students</h3>
                <Link to="/dashboard/school/delegates">
                  <Button className="mt-5 bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold w-full" data-testid="view-students-button">
                    View full roster
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="bg-white border-slate-200">
              <CardContent className="p-6">
                <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                  Quick links
                </p>
                <div className="mt-4 space-y-2">
                  <Link to="/dashboard/events" className="flex items-center justify-between p-3 rounded-md border border-slate-200 hover:border-[#D4AF37]">
                    <span className="text-sm font-semibold text-[#0A192F]">Browse events</span>
                    <CalendarRange className="h-4 w-4 text-[#0F4C81]" />
                  </Link>
                  <Link to="/dashboard/certificates" className="flex items-center justify-between p-3 rounded-md border border-slate-200 hover:border-[#D4AF37]">
                    <span className="text-sm font-semibold text-[#0A192F]">All certificates</span>
                    <Award className="h-4 w-4 text-[#0F4C81]" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Tile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <Icon className="h-4 w-4 text-[#0F4C81]" />
      <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">{label}</p>
      <p className="mt-1 font-serif text-3xl text-[#0A192F]">{value ?? "—"}</p>
    </div>
  );
}
